import { createSelector } from "reselect";
import _get from "lodash/get";
import { deserialize } from "deserialize-json-api/src";

import { getDataComponentFlattenedRequestState } from "../reducers/dataComponentReducer";
import { createDeepEqualSelector } from "./selectors";
import * as REQUEST_TYPES from "../constants/RequestTypes";

const selectorsByDatacomponentId = {};

// Index every resource in the store by `${type}:${id}` so relationship targets
// can be looked up in O(1) instead of scanning the whole store per relationship.
const buildResourceIndex = resources => {
  const index = new Map();
  Object.values(resources).forEach(resourcesByType => {
    if (!resourcesByType || typeof resourcesByType !== "object") return;
    Object.values(resourcesByType).forEach(resource => {
      if (resource && resource.id != null && resource.type != null) {
        index.set(`${resource.type}:${resource.id}`, resource);
      }
    });
  });
  return index;
};

// Building the index walks every resource in the store, and a single dispatched
// action can trigger several getElementsById calls (BW-7020 follow-up: the Quick
// Edit modal alone resolves spec, nextSpec and the purchase order). Redux
// replaces `state.resources` whenever anything is written, so caching against
// that object identity rebuilds exactly once per store version and shares the
// result across every caller. A WeakMap lets superseded stores be collected.
//
// This relies on resource writes producing a new `state.resources` object, which
// is the normal reducer contract — an in-place mutation would leave this stale.
const resourceIndexCache = new WeakMap();

export const getResourceIndex = resources => {
  const cached = resourceIndexCache.get(resources);
  if (cached) return cached;

  const index = buildResourceIndex(resources);
  resourceIndexCache.set(resources, index);
  return index;
};

// Flatten a resource's `relationships` map into the list of { id, type } refs it
// points at, handling both to-one ({ data: {...} }) and to-many ({ data: [...] }).
const getRelationshipRefs = relationships =>
  Object.values(relationships || {}).flatMap(relationship => {
    const relData = relationship && relationship.data;
    if (!relData) return [];
    return Array.isArray(relData) ? relData : [relData];
  });

const refKeyOf = ref =>
  ref && ref.id != null && ref.type != null ? `${ref.type}:${ref.id}` : null;

// Resolve a single relationship ref against the index, pushing newly-discovered
// resources onto the BFS accumulator (visited/included/queue).
const visitRef = (ref, index, acc) => {
  const key = refKeyOf(ref);
  if (!key || acc.visited.has(key)) return;

  const resource = index.get(key);
  if (!resource) return;

  acc.visited.add(key);
  acc.included.push(resource);
  acc.queue.push(resource);
};

// Collect only the resources reachable from `root` through its relationship
// graph. deserialize() ignores any `included` entry it can't reach, so scoping
// the array to the reachable subtree produces identical output while avoiding
// the O(total store) scan-and-copy the library performs per relationship. This
// is what keeps large grids (e.g. 100 deeply-included rows) from re-denormalizing
// against the entire global resource store on every state change.
const collectReachableIncluded = (root, index) => {
  const acc = { visited: new Set(), included: [], queue: [root] };

  while (acc.queue.length) {
    const node = acc.queue.shift();
    getRelationshipRefs(node && node.relationships).forEach(ref =>
      visitRef(ref, index, acc)
    );
  }

  return acc.included;
};

export const getElementsById = (Model, resources, ids) => {
  if (!resources) return [];

  const { currentResources } = Model.query(resources).where({ id: ids });

  if (!currentResources) return [];

  const index = getResourceIndex(resources);

  return Object.values(currentResources).map(
    currentResource =>
      deserialize({
        data: { ...currentResource },
        included: collectReachableIncluded(currentResource, index),
      }).data
  );
};

const createResourcesSelector = () => {
  const dataComponentSelector = createDeepEqualSelector(
    (_, { dataComponent }) => dataComponent,
    dataComponent => dataComponent
  );
  const idsSelector = createDeepEqualSelector(
    (_, { whereIds }) => whereIds,
    whereIds => whereIds
  );
  const resourcesSelector = createSelector(
    state => state.resources,
    dataComponentSelector,
    idsSelector,
    (resources, dataComponent, whereIds) => {
      if (!whereIds) {
        return null;
      }

      // Ids default to all (which is rowIndex array if not supplied explicitly)
      return getElementsById(
        dataComponent.model,
        resources,
        whereIds,
        dataComponent.includes ? dataComponent.includes : []
      ).sort(
        (item1, item2) =>
          whereIds.indexOf(`${item1.id}`) - whereIds.indexOf(`${item2.id}`)
      );
    }
  );

  const deepResourcesSelector = createDeepEqualSelector(
    resourcesSelector,
    resources => resources
  );

  let previousResources = null;

  return (state, props) => {
    const isLoading = _get(props, "dataComponent.loading");
    if (isLoading) return previousResources;

    const newResources = props.useDeep
      ? deepResourcesSelector(state, props)
      : resourcesSelector(state, props);

    return (previousResources = newResources);
  };
};

// How many distinct id sets stay memoized per data component. Reading more than
// one resource from a single data component in the same pass is routine — Quick
// Edit resolves `spec` and `nextSpec` from the same one on every action — and a
// single shared selector made those reads evict each other, so both recomputed
// (re-deserializing against the whole resource store) and both returned a new
// object every time. Four is well past the 2-3 that real call sites use, while
// keeping the cache bounded as paging changes the row index.
const SELECTOR_CACHE_SIZE = 4;

const whereIdsKey = whereIds =>
  Array.isArray(whereIds) ? whereIds.join(",") : `${whereIds}`;

const getResourcesSelectorFor = (dataComponentId, whereIds) => {
  if (!selectorsByDatacomponentId[dataComponentId]) {
    selectorsByDatacomponentId[dataComponentId] = new Map();
  }
  const cache = selectorsByDatacomponentId[dataComponentId];
  const key = whereIdsKey(whereIds);
  const cached = cache.get(key);

  if (cached) {
    // Re-insert so the Map's insertion order doubles as LRU order.
    cache.delete(key);
    cache.set(key, cached);
    return cached;
  }

  const selector = createResourcesSelector();
  if (cache.size >= SELECTOR_CACHE_SIZE) {
    cache.delete(cache.keys().next().value);
  }
  cache.set(key, selector);
  return selector;
};

function _performBluechipQuery(dataComponent, state, whereIds, useDeep) {
  const getResourcesSelector = getResourcesSelectorFor(
    dataComponent.dataComponentId,
    whereIds
  );

  return getResourcesSelector(state, { dataComponent, whereIds, useDeep });
}

function _getWhereIds(dataComponent, ids) {
  if (!dataComponent || !dataComponent.model || dataComponent.error) {
    return null;
  }

  return ids ? ids : dataComponent.rowIndex;
}

export function getResourcesV1(dataComponent, state, ids, useDeep = true) {
  const whereIds = _getWhereIds(dataComponent, ids);
  const resources = _performBluechipQuery(
    dataComponent,
    state,
    whereIds,
    useDeep
  );
  if (!resources || !resources.length) return null;

  return resources;
}

export function getResourcesV2(
  dataComponent,
  state,
  requestType = dataComponent.requestType,
  defaultValue
) {
  return _get(
    state.dataComponentResources,
    `${dataComponent.dataComponentId}_${requestType}`,
    { data: defaultValue }
  ).data;
}

export function getResourceLinks(resource) {
  const links = _get(resource, "__json_api__.links", {});
  return Object.keys(links).reduce((resourceLinks, linkKey) => {
    resourceLinks[linkKey] = _get(links[linkKey], "meta.id");
    return resourceLinks;
  }, {});
}

export function getBluechipResources(
  dataComponent,
  state,
  ids,
  useDeep = true
) {
  if (dataComponent.dataComponentVersion === "v2") {
    return getResourcesV2(dataComponent, state);
  }
  return getResourcesV1(dataComponent, state, ids, useDeep);
}

export function getCurrentBluechipResourcesForRequestType(
  dataComponentId,
  state,
  requestType = REQUEST_TYPES.LIST
) {
  const dataComponent = getDataComponentFlattenedRequestState(
    dataComponentId,
    state,
    requestType
  );
  const resources = getBluechipResources(dataComponent, state);
  if (!resources) {
    return null;
  }

  if (
    requestType === REQUEST_TYPES.LIST ||
    requestType === REQUEST_TYPES.CREATE
  ) {
    return resources;
  }

  return resources[0];
}

//Create an alias for method
export const getBluechipResourcesByType = getCurrentBluechipResourcesForRequestType;

export function getCurrentResources(state, dataComponentIdOperationMap) {
  const resourceMap = {};
  Object.entries(dataComponentIdOperationMap).forEach(
    ([dataComponentId, operation]) => {
      resourceMap[dataComponentId] = getCurrentBluechipResourcesForRequestType(
        dataComponentId,
        state,
        operation
      );
    }
  );

  return resourceMap;
}

const isValidRequest = (dataComponent, id) => {
  return dataComponent && dataComponent.model && !dataComponent.error && id;
};

export function getBluechipResourceById(dataComponent, state, id) {
  if (dataComponent.dataComponentVersion === "v2") {
    return getResourcesV2(dataComponent, state, REQUEST_TYPES.FIND);
  }
  if (!isValidRequest(dataComponent, id)) {
    return null;
  }

  const resources = getBluechipResources(dataComponent, state, [`${id}`]);

  return resources ? resources[0] : null;
}

export function findItemInResources(dataComponent, state, id) {
  if (dataComponent.dataComponentVersion === "v2") {
    const resources =
      getResourcesV2(dataComponent, state, REQUEST_TYPES.LIST) || [];
    return resources.find(resource => resource.id === id);
  }
  return getBluechipResourceById(dataComponent, state, id);
}

export function findItemsInResources(dataComponent, state, ids) {
  const resources =
    getResourcesV2(dataComponent, state, REQUEST_TYPES.LIST) || [];

  return resources.filter(resource => ids.includes(resource.id));
}

export function getNextId(dataComponent, state, id) {
  if (!dataComponent || !dataComponent.model || dataComponent.error) {
    return null;
  }
  const currentId = id || dataComponent.rowIndex[0];
  return _get(
    state,
    `resources.${dataComponent.model.pluralName()}.${currentId}.links.next.meta.id`
  );
}
